import { useMemo, useState } from 'react'
import {
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import type { HazardInfo } from '../../../types/climate'
import ClimateControlField from './ClimateControlField'
import { getClimateHazardVisualMeta } from './hazardConfig'

type HazardSelectorProps = {
  hazards: HazardInfo[]
  value: string
  onChange: (hazardCode: string) => void
  label?: string
}

const HazardSelector = ({ hazards, value, onChange, label = 'Hazard' }: HazardSelectorProps) => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const selectedHazard = useMemo(
    () => hazards.find(hazard => hazard.name === value) ?? hazards[0] ?? null,
    [hazards, value],
  )
  const open = Boolean(anchorEl)
  const pickerWidth = anchorEl ? Math.max(anchorEl.clientWidth, 320) : 360

  const closePicker = () => setAnchorEl(null)

  const hazardList = (
    <Stack spacing={1} sx={{ p: 1 }}>
      {hazards.map(hazard => {
        const meta = getClimateHazardVisualMeta(hazard.name)
        const selected = hazard.name === selectedHazard?.name

        return (
          <ButtonBase
            key={hazard.name}
            onClick={() => {
              onChange(hazard.name)
              closePicker()
            }}
            sx={{
              width: '100%',
              borderRadius: 2,
              border: '1px solid',
              borderColor: selected ? 'primary.main' : 'divider',
              backgroundColor: selected ? 'action.selected' : 'background.paper',
              px: 1.25,
              py: 1.1,
              textAlign: 'left',
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              '&:hover': {
                backgroundColor: selected ? 'action.selected' : 'action.hover',
              },
            }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: '100%' }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  backgroundColor: meta.accent,
                  color: meta.tone,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}>
                {meta.icon}
              </Box>
              <Stack spacing={0.25} minWidth={0}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {hazard.display_name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.3,
                  }}>
                  {hazard.description || undefined}
                </Typography>
              </Stack>
            </Stack>
          </ButtonBase>
        )
      })}
    </Stack>
  )

  return (
    <>
      <ClimateControlField label={label}>
        <ButtonBase
          onClick={event => setAnchorEl(event.currentTarget)}
          aria-label="Open climate hazard picker"
          sx={{
            width: '100%',
            minHeight: 40,
            borderRadius: 2.5,
            backgroundColor: 'action.hover',
            px: 1.25,
            py: 0.75,
            justifyContent: 'space-between',
            '&:hover': {
              backgroundColor: 'action.selected',
            },
          }}>
          <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
            {selectedHazard && (
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: getClimateHazardVisualMeta(selectedHazard.name).accent,
                  color: getClimateHazardVisualMeta(selectedHazard.name).tone,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}>
                {getClimateHazardVisualMeta(selectedHazard.name).icon}
              </Box>
            )}
            <Typography variant="body2" fontWeight={700} noWrap>
              {selectedHazard?.display_name ?? 'Select hazard'}
            </Typography>
          </Stack>
          <KeyboardArrowDownRoundedIcon fontSize="small" color="action" />
        </ButtonBase>
      </ClimateControlField>

      {isSmallScreen ? (
        <Dialog
          open={open}
          onClose={closePicker}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: { xs: '100%', sm: 420 },
              m: { xs: 0, sm: 2 },
              borderRadius: { xs: '24px 24px 0 0', sm: 3 },
              position: { xs: 'fixed', sm: 'relative' },
              bottom: { xs: 0, sm: 'auto' },
            },
          }}>
          <DialogTitle>Select Hazard</DialogTitle>
          <DialogContent dividers={false} sx={{ px: 1, pb: 1.5 }}>
            {hazardList}
          </DialogContent>
        </Dialog>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={closePicker}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              mt: 1,
              width: `${pickerWidth}px`,
              maxWidth: 'min(420px, calc(100vw - 32px))',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: theme.shadows[6],
            },
          }}>
          {hazardList}
        </Popover>
      )}
    </>
  )
}

export default HazardSelector
